import { ApiError, type ClientErrorCode } from "./client";

/** Plain-language explanations for stable API error codes. Shown in toasts and inline. */
const FRIENDLY: Partial<Record<ClientErrorCode, string>> = {
  BadRequest: "The request was not accepted. Check the values and try again.",
  ValidationFailed: "Some values are not valid. Check the highlighted fields.",
  Unauthenticated: "Your session has ended. Please sign in again.",
  InvalidLogin: "Email or password is incorrect.",
  TokenExpired: "Your session has expired. Please sign in again.",
  Forbidden: "You don't have permission to do that.",
  OutOfScope: "This robot or resource is outside the sites/fleets you are assigned to.",
  NotFound: "That item no longer exists.",
  Conflict: "Someone else changed this at the same time. Reload and try again.",
  IdempotencyKeyReused: "This request was already submitted with different details. Start a new request.",
  RobotOffline: "The robot is offline. The request can't be delivered until it reconnects.",
  RobotNotReconciled: "The robot just reconnected and is still syncing. Try again in a few seconds.",
  RobotBusy: "The robot is busy with another task. Wait for it to finish or cancel it first.",
  OutcomeUnresolved: "A previous command's outcome on this robot is unknown. A technician must check the robot and resolve it before new work can start.",
  CapabilityMissing: "This robot doesn't support that function.",
  InterfaceNotAllowed: "That robot function isn't enabled in the interface registry.",
  BoundsViolation: "A value is outside the allowed safe range.",
  ControlLeaseRequired: "You need to take control of the robot first.",
  ControlHeldByOther: "Another user is controlling this robot.",
  ControlNotInstalled:
    "Waiting for the robot to confirm control (takeover pending). Nothing is sent to the robot until it does — try again in a moment; if the robot is offline it confirms when it reconnects.",
  EstopActive: "The emergency stop is engaged. Release it before continuing.",
  BundleInvalid: "The configuration has problems that must be fixed before it can be deployed.",
  ConfigOutOfDate: "The robot's configuration is out of date. Deploy the latest map/routes first.",
  RateLimited: "Too many requests. Please wait a moment.",
  ArmJogNotConfirmed: "Confirm that you can see the robot and the space around the arm before jogging.",
  InternalError: "Something went wrong on the platform. Try again; if it persists, contact support.",
  CommsUnavailable: "The robot communication service is unavailable right now.",
  NetworkError: "Could not reach the platform. Check your network connection.",
  UnexpectedResponse: "The platform returned an unexpected response.",
};

export function friendlyMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const base = FRIENDLY[err.errorCode];
    if (err.errorCode === "BoundsViolation" || err.errorCode === "ValidationFailed" || err.errorCode === "BundleInvalid") {
      // These carry useful specifics from the server.
      return base ? `${base} (${err.message})` : err.message;
    }
    // The server uses Conflict for many specific refusals (map deployed, waypoint referenced, name taken…).
    if (err.errorCode === "Conflict" && err.message) return err.message;
    if (err.errorCode === "ControlHeldByOther") {
      const holder = (err.errorData as { principalName?: string } | undefined)?.principalName;
      if (holder) return `${holder} is controlling this robot.`;
    }
    return base ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

export function errorTitle(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.errorCode) {
      case "Forbidden":
      case "OutOfScope":
        return "Not allowed";
      case "EstopActive":
        return "E-stop engaged";
      case "ControlHeldByOther":
      case "ControlLeaseRequired":
        return "Control required";
      case "ControlNotInstalled":
        return "Control not confirmed yet";
      case "RobotOffline":
      case "RobotNotReconciled":
      case "CommsUnavailable":
        return "Robot unavailable";
      case "NetworkError":
        return "Connection problem";
      default:
        return "Request failed";
    }
  }
  return "Error";
}
